import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * Timezone Controller
 * 
 * Detects and stores the user's browser timezone in a cookie.
 * Optionally reloads the page when timezone is first detected or changes.
 * 
 * @extends Controller
 * @property {boolean} requireReloadValue - Whether to reload page when timezone changes
 * 
 * @example
 * <div data-controller="pwa--timezone" 
 *      data-pwa--timezone-require-reload-value="true">
 */
export default class extends Controller {
  static values = { 
    requireReload: Boolean 
  }

  /**
   * Detect browser timezone and store in cookie
   * Reloads page if required and timezone has changed
   */
  connect() {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!tz) return
    
    const currentTzCookie = this.getCookie("user_time_zone")
    
    // Exit if timezone hasn't changed
    if (currentTzCookie === tz) return
    
    // Update the cookie with 1 year expiration
    document.cookie = `user_time_zone=${encodeURIComponent(tz)}; path=/; max-age=31536000`
    
    // Reload if required (for pages that need timezone info)
    if (!this.requireReloadValue) return
    
    Turbo.visit(window.location.href, { action: "replace" })
  }
  
  /**
   * Get cookie value by name
   * 
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  }
}